import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import VariableSelector from "../components/VariableSelector";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadFull } from "tsparticles";
import BillingPage from "../components/BillingPage";
import { v4 as uuidv4 } from 'uuid';

console.log("🌎 API Base URL:", process.env.REACT_APP_API_URL);

const Index = () => {
  const navigate = useNavigate();
  
  // 🔹 Centralized billing state (prevents race conditions)
  const [billingState, setBillingState] = useState({
    plan: null,
    summariesLeft: 0,
    subscription_status: "none",  // "active" | "canceled" | "none"
    isUserRegistered: false
  });
  const [billingLoaded, setBillingLoaded] = useState(false);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState("");
  const [selectedVariables, setSelectedVariables] = useState([]);
  const [summaryMode, setSummaryMode] = useState("general");
  const [init, setInit] = useState(false); // 👈 para inicializar partículas
  const [userId, setUserId] = useState(null);
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [portalMessage, setPortalMessage] = useState({ type: null, text: null });
  const fileInputRef = useRef(null);
  
  // 🔹 Derived values from centralized state
  const { plan: userPlan, summariesLeft: remainingSummaries, subscription_status } = billingState;

  // 🔹 Unified function to update billing state atomically
  const updateBillingState = (data) => {
    setBillingState(prev => ({
      plan: data?.plan || prev.plan || null,
      summariesLeft: typeof data?.remaining_summaries === "number" 
        ? data.remaining_summaries 
        : prev.summariesLeft || 0,
      subscription_status: data?.subscription_status || prev.subscription_status || "none",
      isUserRegistered: !!data?.user_id
    }));
  };

  // 🔹 Cargar estado del usuario desde el backend al montar
  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const storedUserId = localStorage.getItem("user_id");
        setUserId(storedUserId);

        const API_URL = process.env.REACT_APP_API_URL || "https://api.nuclearenergyagent.com";
        if (!storedUserId) {
          console.warn("⚠️ No user_id found in localStorage, skipping status check.");
          setBillingLoaded(true);
          return;
        }

        const response = await fetch(`${API_URL}/api/users/status/${storedUserId}`, {
          credentials: "include",
        });

        if (!response.ok) {
          console.warn("⚠️ Could not fetch user status:", response.status);
          setBillingLoaded(true);
          return;
        }

        const data = await response.json();
        
        // ✅ Update billing state atomically
        updateBillingState(data);
      } catch (error) {
        console.error("❌ Error fetching user status:", error);
      } finally {
        // ✅ Mark as loaded to prevent race conditions
        setBillingLoaded(true);
      }
    };

    fetchUserStatus();
  }, []);

  // 🔹 Unified function to refresh billing state from backend
  const refreshUserStatus = async () => {
    const storedUserId = localStorage.getItem("user_id");
    if (!storedUserId) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.nuclearenergyagent.com";
      const res = await fetch(`${API_BASE_URL}/api/users/status/${storedUserId}`);
      
      if (res.ok) {
        const data = await res.json();
        // ✅ Update billing state atomically (prevents race conditions)
        updateBillingState(data);
      }
    } catch (error) {
      console.error("Error refreshing user status:", error);
    }
  };

  // 🔄 Refrescar plan automáticamente después de pago exitoso
  useEffect(() => {
    const refreshFlag = localStorage.getItem("refresh_after_payment");
    if (refreshFlag === "true") {
      refreshUserStatus();
      localStorage.removeItem("refresh_after_payment");
    }
  }, []);

  // 🔹 Función para abrir Customer Portal de Stripe
  const handleManageSubscription = async () => {
    // 🔹 Prevent portal call if subscription is inactive
    if (isSubscriptionInactive || !hasSubscriptionPlan) {
      setPortalMessage({
        type: "error",
        text: "Your subscription is inactive. Please resubscribe to manage your subscription."
      });
      setTimeout(() => setPortalMessage({ type: null, text: null }), 5000);
      return;
    }

    const storedUserId = localStorage.getItem("user_id");
    if (!storedUserId) {
      setPortalMessage({
        type: "error",
        text: "Please activate the service first."
      });
      setTimeout(() => setPortalMessage({ type: null, text: null }), 5000);
      return;
    }

    setIsLoadingPortal(true);
    setPortalMessage({ type: null, text: null });
    
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.nuclearenergyagent.com";
      const endpoint = `${API_BASE_URL}/api/stripe/customer-portal`;
      
      console.log("🔍 Opening Customer Portal for user:", storedUserId);
      console.log("🔍 API Endpoint:", endpoint);
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ user_id: storedUserId }),
      });

      console.log("🔍 Response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || errorData.error || `HTTP ${response.status}`;
        
        console.error("❌ Customer Portal error:", errorMsg);
        
        if (response.status === 400 && errorMsg.includes("Credit packs")) {
          setPortalMessage({
            type: "error",
            text: "Customer Portal is only available for subscription plans. Credit packs are one-time purchases."
          });
        } else if (response.status === 404) {
          if (errorMsg.includes("User not found")) {
            setPortalMessage({
              type: "error",
              text: "Your account was not found. Please contact support."
            });
          } else {
            setPortalMessage({
              type: "error",
              text: "Your subscription has been canceled or is no longer active."
            });
          }
        } else if (response.status === 500) {
          setPortalMessage({
            type: "error",
            text: "Server error. Please try again later or contact support."
          });
        } else {
          setPortalMessage({
            type: "error",
            text: `Unable to open Customer Portal: ${errorMsg}`
          });
        }
        setIsLoadingPortal(false);
        setTimeout(() => setPortalMessage({ type: null, text: null }), 7000);
        return;
      }

      const data = await response.json();
      console.log("✅ Customer Portal response received:", data);
      
      if (data.portal_url) {
        console.log("✅ Stripe Customer Portal URL received:", data.portal_url);
        window.location.href = data.portal_url;
      } else {
        console.error("❌ No portal_url in response:", data);
        setPortalMessage({
          type: "error",
          text: "No portal URL received from server. Please try again."
        });
        setIsLoadingPortal(false);
        setTimeout(() => setPortalMessage({ type: null, text: null }), 5000);
      }
    } catch (error) {
      console.error("❌ Error opening Customer Portal:", error);
      
      // Handle network errors
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        setPortalMessage({
          type: "error",
          text: "Network error. Please check your connection and try again."
        });
      } else {
        setPortalMessage({
          type: "error",
          text: "Error opening Customer Portal. Please try again later."
        });
      }
      setIsLoadingPortal(false);
      setTimeout(() => setPortalMessage({ type: null, text: null }), 5000);
    }
  };

  // 🔥 Forzar refresh del plan cuando regresa de Stripe
  useEffect(() => {
    const checkRefresh = () => {
      const flag = localStorage.getItem("refresh_after_payment");
      if (flag === "true") {
        refreshUserStatus();
        localStorage.removeItem("refresh_after_payment");
      }
    };

    // Listener de eventos del localStorage (cuando SuccessPage marca la bandera)
    window.addEventListener("storage", checkRefresh);
    // Listener cuando la ventana vuelve a tener foco
    window.addEventListener("focus", checkRefresh);
    // Listener cuando la página se hace visible (regreso de Stripe)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkRefresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    
    // Ejecutar inmediatamente
    checkRefresh();

    return () => {
      window.removeEventListener("storage", checkRefresh);
      window.removeEventListener("focus", checkRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔹 Lógica mejorada para banners de estado
  // Crear objeto user derivado del estado centralizado
  const user = {
    plan: billingState.plan || "Free",
    summaries_used: Math.max(0, 5 - (billingState.summariesLeft || 0))
  };

  // 🔹 Determine subscription state from backend truth
  // Active = subscription_status === "active"
  // Inactive = subscription_status === "canceled" or "none"
  const isSubscriptionActive = billingState.subscription_status === "active";
  const isSubscriptionInactive = billingState.subscription_status === "canceled" || billingState.subscription_status === "none";
  
  // 🔹 Usage control: ONLY credits matter, NOT subscription status
  const canUseService = billingState.summariesLeft > 0;
  const isSubscribed = billingState.subscription_status === "active";
  
  // 🔹 Check if user has a subscription plan (not trial or credit pack)
  const hasSubscriptionPlan = billingState.plan === "pro_subscription" || billingState.plan === "lab_pro_unlimited";
  
  // 🔹 For inactive state, only show message if user had a subscription plan
  const shouldShowInactiveMessage = isSubscriptionInactive && hasSubscriptionPlan;
  
  // 🔹 Check if user has canceled subscription but still has remaining credits
  const hasCanceledSubscriptionWithCredits = isSubscriptionInactive && hasSubscriptionPlan && billingState.summariesLeft > 0;

  const isTrialUser =
    billingState.plan === "Free" ||
    billingState.plan === "trial_plan" ||
    !billingState.plan;
  const trialLimitReached = isTrialUser && billingState.summariesLeft <= 0;
  const trialRemaining = isTrialUser ? Math.max(0, billingState.summariesLeft || 0) : null;

  // 🔔 Toast de activación después de upgrade
  const [showActivationToast, setShowActivationToast] = useState(false);
  const prevPlanRef = useRef(null);

  useEffect(() => {
    // Detecta si el usuario acaba de pasar de Free/Trial a plan de pago
    if (prevPlanRef.current && prevPlanRef.current !== billingState.plan && billingState.plan && billingState.plan !== "Free" && billingState.plan !== "trial_plan") {
      setShowActivationToast(true);
      // Auto-cerrar después de 4s
      setTimeout(() => setShowActivationToast(false), 4000);
    }
    prevPlanRef.current = billingState.plan;
  }, [billingState.plan]);

  // Inicializar partículas con la librería moderna
  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadFull(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);


  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleFileSelect = (file) => {
    if (
      file &&
      (file.type === "application/pdf" ||
        file.type === "text/plain" ||
        file.type === "text/csv" ||
        file.name.endsWith(".pdf") ||
        file.name.endsWith(".txt") ||
        file.name.endsWith(".csv"))
    ) {
      setSelectedFile(file);
    } else {
      alert("Please select a valid file (PDF, TXT, or CSV)");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
      e.target.value = null;
    }
  };

  const handleActivateService = async () => {
    try {
      // Si el usuario ya tiene plan de pago, no hace nada
      if (user?.plan && user?.plan !== "Free") {
        console.log("User already has a paid plan; skipping trial activation.");
        return;
      }

      // Generar o recuperar user_id
      let storedUserId = localStorage.getItem("user_id");
      if (!storedUserId) {
        storedUserId = uuidv4();
        localStorage.setItem("user_id", storedUserId);
      }

      // Registrar usuario en backend
      const API_URL = process.env.REACT_APP_API_URL || "https://api.nuclearenergyagent.com";
      const response = await fetch(`${API_URL}/api/users/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: storedUserId }),
      });

      if (!response.ok) {
        alert("⚠️ Could not activate trial. Please try again later.");
        console.warn("⚠️ Register user failed:", response.status);
        return;
      }

      const data = await response.json();
      console.log("✅ Trial user registered:", data);

      // ✅ Update billing state atomically
      setUserId(storedUserId);
      updateBillingState({
        plan: data.user?.plan || "trial_plan",
        remaining_summaries: data.user?.remaining_summaries || 5,
        subscription_status: "none",  // Trial is not a subscription
        user_id: storedUserId
      });

      alert("✅ Free Trial activated! You have 5 summaries available.");
      await refreshUserStatus(); // 🔄 actualiza banner inmediatamente
    } catch (error) {
      console.error("❌ Error activating service:", error);
      alert("❌ Error activating service. Check console for details.");
    }
  };

  const handleProcessFile = async () => {
    if (!selectedFile) {
      alert("Please select a file first.");
      return;
    }

    if (!userId) {
      alert("Please activate the service first by clicking 'Activate Service'.");
      return;
    }

    // ✅ Check credits before processing (decoupled from subscription status)
    // Usage is ONLY blocked by credits, not subscription status
    if (billingState.summariesLeft <= 0) {
      alert("You've reached your credit limit. Please purchase more credits or upgrade to continue.");
      window.location.href = "https://nuclearenergyagent.com/pricing";
      return;
    }

    setIsProcessing(true);
    setResult("");

    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://api.nuclearenergyagent.com';

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mode", summaryMode);
      selectedVariables.forEach((v) => formData.append("variables", v));

      const response = await fetch(`${apiBaseUrl}/api/agent/summarize`, {
        method: "POST",
        headers: {
          "X-User-ID": userId  // ✅ Include user ID header
        },
        body: formData,
      });

      // ✅ Handle trial limit reached
      if (response.status === 403) {
        const errorData = await response.json();
        alert(`Trial limit reached: ${errorData.detail}`);
        window.location.href = "https://nuclearenergyagent.com/pricing";
        return;
      }

      try {
        const data = await response.json();
        console.log("Full JSON Response:", JSON.stringify(data, null, 2));

        if (data.summary) {
          setResult(data.summary);
        } else if (data.Problem || data.Method || data.Results || data.Conclusion) {
          const parts = [];
          if (data.Problem) parts.push(`Problem: ${data.Problem}`);
          if (data.Method) parts.push(`Method: ${data.Method}`);
          if (data.Results) parts.push(`Results: ${data.Results}`);
          if (data.Conclusion) parts.push(`Conclusion: ${data.Conclusion}`);
          setResult(parts.join("\n\n"));
        } else {
          setResult("No summary could be generated.");
        }

        // ✅ Sincronizar estado desde el backend después de procesar exitosamente
        // This updates remainingSummaries immediately after successful processing
        await refreshUserStatus();
      } catch (err) {
        const text = await response.text();
        console.error("Response was not JSON:", text);
        setResult("No summary could be generated.");
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setResult("No summary could be generated.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  // return (
  //   <div className="min-h-screen bg-[#0b0f19] text-white flex items-center justify-center">
  //     <BillingPage />
  //   </div>
  // );
  

  // ✅ Log final billing state once (only when loaded and state changes)
  useEffect(() => {
    if (billingLoaded) {
      console.log("✅ Billing state:", {
        plan: billingState.plan,
        summariesLeft: billingState.summariesLeft,
        subscription_status: billingState.subscription_status,
        isUserRegistered: billingState.isUserRegistered
      });
    }
  }, [billingLoaded, billingState.plan, billingState.summariesLeft, billingState.subscription_status]);
  
  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 py-8 px-4 text-white">
      {init && (
        <Particles
          id="tsparticles"
          className="pointer-events-none absolute inset-0 z-0"
          options={{
            background: { color: "transparent" },
            fpsLimit: 60,
            interactivity: {
              events: {
                onClick: { enable: true, mode: "push" },
                onHover: { enable: true, mode: "repulse" },
                resize: true,
              },
              modes: {
                push: { quantity: 4 },
                repulse: { distance: 100, duration: 0.4 },
              },
            },
            particles: {
              color: { value: "#00f0ff" },
              links: {
                color: "#00f0ff",
                distance: 150,
                enable: true,
                opacity: 0.3,
                width: 1,
              },
              collisions: { enable: false },
              move: {
                direction: "none",
                enable: true,
                outModes: { default: "bounce" },
                random: false,
                speed: 1,
                straight: false,
              },
              number: {
                density: { enable: true, area: 800 },
                value: 60,
              },
              opacity: { value: 0.6 },
              shape: { type: "circle" },
              size: { value: { min: 1, max: 3 } },
            },
            detectRetina: true,
          }}
        />
      )}

      {/* 🔔 Service Activated Toast */}
      {showActivationToast && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-700 text-white px-6 py-3 rounded-xl shadow-lg z-50 flex items-center gap-4">
          <span>✅ Service Activated — Welcome back!</span>
          <button
            onClick={() => setShowActivationToast(false)}
            className="bg-green-900 hover:bg-green-800 text-white px-3 py-1 rounded-lg text-sm"
          >
            OK
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-extrabold text-cyan-400 mb-4">
            Nuclear Energy Agent
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Turning complex nuclear data into clear insights — instantly.
          </p>
          {/* 🔹 Unified Plan / Trial Banner (only after billing is loaded) */}
          {billingLoaded && billingState.isUserRegistered && billingState.plan && (
            <div className="mb-4 space-y-3">
              {/* 🔹 ACTIVE Subscription State */}
              {isSubscriptionActive && (
                <>
                  <div className="bg-green-900 text-center py-3 rounded-lg transition-opacity duration-300">
                    <p className="text-green-300 font-semibold">
                      {isTrialUser ? (
                        <>
                          You have{" "}
                          <span className="font-bold text-green-200">
                            {billingState.summariesLeft}
                          </span>{" "}
                          summaries left —{" "}
                          <span className="italic text-green-400">Trial Mode</span>
                        </>
                      ) : (
                        <>
                          Your plan is{" "}
                          <span className="font-bold text-green-200">
                            {billingState.plan.replaceAll("_", " ")}
                          </span>{" "}
                          —{" "}
                          <span className="font-bold text-green-200">
                            {billingState.summariesLeft}
                          </span>{" "}
                          summaries left
                        </>
                      )}
                    </p>
                  </div>
                  
                  {/* 🔹 Manage Subscription Button (only for subscription plans) */}
                  {!isTrialUser && hasSubscriptionPlan && (
                    <div className="text-center">
                      <button
                        onClick={handleManageSubscription}
                        disabled={isLoadingPortal || isSubscriptionInactive}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoadingPortal ? "Loading..." : "Manage Subscription"}
                      </button>
                      <p className="text-gray-400 text-sm mt-2">
                        You can cancel or update your subscription at any time.
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* 🔹 INACTIVE Subscription State (only for subscription plans) */}
              {shouldShowInactiveMessage && (
                <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-6 text-center">
                  <h3 className="text-xl font-semibold text-yellow-300 mb-2">
                    Your subscription is inactive
                  </h3>
                  <p className="text-yellow-200 mb-4">
                    You can resubscribe at any time.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg shadow-md transition-all"
                  >
                    Resubscribe
                  </button>
                </div>
              )}

              {/* 🔹 Canceled Subscription with Remaining Credits */}
              {hasCanceledSubscriptionWithCredits && (
                <div className="bg-orange-900/50 border border-orange-600 rounded-lg p-4 text-center mb-4">
                  <p className="text-orange-200 font-semibold">
                    Subscription canceled — using remaining summaries ({billingState.summariesLeft} left)
                  </p>
                </div>
              )}

              {/* 🔹 Inline Message Display */}
              {portalMessage.type && portalMessage.text && (
                <div className={`rounded-lg p-4 text-center ${
                  portalMessage.type === "error" 
                    ? "bg-red-900/50 border border-red-600 text-red-200" 
                    : "bg-green-900/50 border border-green-600 text-green-200"
                }`}>
                  <p className="font-semibold">{portalMessage.text}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-gray-900/80 backdrop-blur-sm border border-blue-500 rounded-2xl shadow-lg hover:shadow-blue-500/50 p-8">
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">
              Upload Your Document
            </h2>
            <div
              className={`border-2 border-cyan-500 rounded-xl p-8 text-center bg-gray-900 shadow-lg hover:shadow-cyan-500/50 transition ${
                isDragOver
                  ? "border-cyan-400 bg-cyan-500/10"
                  : "border-cyan-500 hover:border-cyan-400"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {selectedFile ? (
                <div className="space-y-4">
                  <div className="text-green-400">
                    <svg
                      className="w-16 h-16 mx-auto mb-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-lg font-medium text-white">
                      File Selected
                    </p>
                    <p className="text-sm text-gray-300">{selectedFile.name}</p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-red-400 hover:text-red-300 text-sm font-medium"
                  >
                    Remove File
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <svg
                    className="w-16 h-16 mx-auto text-blue-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <div>
                    <p className="text-lg font-medium text-white">
                      Drop your file here, or{" "}
                      <button
                        onClick={openFileDialog}
                        className="text-blue-400 hover:text-blue-300 font-medium"
                      >
                        browse
                      </button>
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Supports PDF, TXT, and CSV files
                    </p>
                  </div>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt,.csv"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">
              Summary Mode
            </h2>
            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setSummaryMode("general")}
                className={`px-4 py-2 rounded-lg font-medium ${
                  summaryMode === "general"
                    ? "bg-cyan-600 text-white shadow-md"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                General
              </button>
              <button
                onClick={() => setSummaryMode("variables")}
                className={`px-4 py-2 rounded-lg font-medium ${
                  summaryMode === "variables"
                    ? "bg-cyan-600 text-white shadow-md"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                By Variables
              </button>
              <button
                onClick={() => setSummaryMode("mixed")}
                className={`px-4 py-2 rounded-lg font-medium ${
                  summaryMode === "mixed"
                    ? "bg-cyan-600 text-white shadow-md"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                }`}
              >
                Mixed
              </button>
            </div>
            <p className="text-sm text-gray-300 mt-2">
              Choose how you want the summary to be generated: <br />
              <span className="font-semibold text-blue-400">General</span> →
              Creates a classic scientific abstract (Problem → Method → Results
              → Conclusion). <br />
              <span className="font-semibold text-blue-400">By Variables</span> →
              Extracts and organizes all values for the variables you selected.{" "}
              <br />
              <span className="font-semibold text-blue-400">Mixed</span> →
              Combines both: a general abstract + automatically detected key
              variables.
            </p>
          </div>

          <div className="mb-8">
            <VariableSelector onVariablesChange={setSelectedVariables} />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <button
              onClick={handleActivateService}
              disabled={!isTrialUser}
              className={`flex-1 px-8 py-4 rounded-xl font-semibold text-lg ${
                !isTrialUser
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-600 text-white"
              }`}
            >
              Activate Service
            </button>

            <button
              onClick={handleProcessFile}
              disabled={
                !selectedFile ||
                isProcessing ||
                !billingLoaded ||
                billingState.summariesLeft <= 0
              }
              className={`flex-1 px-8 py-4 rounded-xl font-semibold text-lg ${
                !selectedFile ||
                isProcessing ||
                !billingLoaded ||
                billingState.summariesLeft <= 0
                  ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white"
              }`}
            >
              {isProcessing ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                "Process File"
              )}
            </button>
          </div>

          {trialLimitReached && (
            <div className="mt-3 w-full bg-orange-500 text-white text-center py-2 rounded-lg font-semibold">
              Trial limit reached —{" "}
              <button
                onClick={() => {
                  // ✅ Mantiene el user_id en localStorage y navega dentro de la misma pestaña
                  window.location.assign("/pricing");
                }}
                className="underline ml-1 font-bold"
              >
                Upgrade Plan
              </button>
            </div>
          )}

          <div className="border-t border-gray-700 pt-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Results</h2>
            <div className="bg-gray-900 rounded-xl p-8 text-center border-2 border-cyan-800">
              {result ? (
                <div className="results-box">
                  <p className="text-gray-200 text-left whitespace-pre-wrap">
                    {result}
                  </p>
                </div>
              ) : (
                <>
                  <svg
                    className="w-16 h-16 mx-auto text-blue-400 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p className="text-gray-300 text-lg">
                    {!billingLoaded
                      ? "Loading billing information..."
                      : !selectedFile
                      ? "Upload a file to see extracted results"
                      : billingState.summariesLeft <= 0
                      ? "Credit limit reached - purchase more credits or upgrade to continue"
                      : 'Click "Process File" to analyze your document'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer – Contact Info */}
      <div className="mt-16 pb-8 text-center text-gray-400 text-sm">
        <p>
          Contact Us:{" "}
          <a
            href="mailto:support@nuclearenergyagent.com"
            className="text-cyan-400 hover:text-cyan-300 underline"
          >
            support@nuclearenergyagent.com
          </a>
        </p>
      </div>

      <div className="text-center text-gray-500 mt-8 text-sm pb-8">
        © 2025 NuclearEnergyAgent.com — All Rights Reserved.
        <div className="mt-2">
          <a href="https://nuclearenergyagent.com/privacy-policy" className="text-blue-400 hover:underline mr-4">
            Privacy Policy
          </a>
          |
          <a href="https://nuclearenergyagent.com/terms" className="text-blue-400 hover:underline ml-4">
            Terms of Service
          </a>
        </div>
      </div>
    </div>
  );

};

export default Index;
